import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { usePageSEO } from '@/hooks/usePageSEO';
import { getWebPageSchema, getBreadcrumbSchema, siteDomain } from '@/utils/schema';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const ITEMS_PER_PAGE = 6;

export default function CategorySlugPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(slug) : [];
  const totalPages = Math.ceil(articles.length / ITEMS_PER_PAGE);
  const paginatedArticles = articles.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  usePageSEO({
    title: category
      ? `${category.name} - kaiyuntiyu | 体育资讯·赛事数据·专业分析`
      : '分类 - kaiyuntiyu',
    description: category
      ? `${category.description.slice(0, 100)}`
      : '浏览体育分类资讯',
    keywords: `kaiyuntiyu,kaiyuntiyu${category?.name || ''},kaiyuntiyu体育`,
    canonicalUrl: `${siteDomain}/category/${slug}`,
    schema: category
      ? [
          getWebPageSchema(
            `${category.name} - kaiyuntiyu`,
            category.description,
            `${siteDomain}/category/${slug}`
          ),
          getBreadcrumbSchema([
            { name: '首页', url: siteDomain },
            { name: '全部分类', url: `${siteDomain}/category` },
            { name: category.name, url: `${siteDomain}/category/${slug}` },
          ]),
        ]
      : undefined,
  });

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 text-center py-20">
          <h1 className="text-2xl font-bold text-foreground-950 font-heading mb-3">分类未找到</h1>
          <Link to="/category" className="text-primary-600 hover:text-primary-700 text-sm no-underline">
            返回全部分类
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-16">
        <Breadcrumb items={[
          { name: '首页', path: '/' },
          { name: '全部分类', path: '/category' },
          { name: category.name },
        ]} />
      </div>

      <main>
        {/* Category Header */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-primary-100/70 rounded-2xl">
                <i className={`${category.icon} text-primary-600 text-3xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-1">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600">
                  {category.articleCount} 篇文章
                </p>
              </div>
            </div>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {category.description}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full pb-4 bg-background-50 border-b border-background-200/70 sticky top-16 z-40 bg-background-50/95 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2">
              <Link
                to="/category"
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap no-underline"
              >
                全部分类
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap no-underline ${
                    cat.slug === slug
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {paginatedArticles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-secondary-100/70 rounded-2xl">
                  <i className="ri-article-line text-secondary-500 text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-2">
                  暂无文章
                </h3>
                <p className="text-sm text-foreground-500">该分类下暂无文章，请稍后再来</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paginatedArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                    >
                      <div className="relative w-full h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                            {category.name}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors font-heading">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-user-line"></i> {article.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i> {(article.views / 1000).toFixed(1)}k
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer bg-transparent"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer border-0 ${
                          page === currentPage
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:bg-background-100 border border-background-300'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer bg-transparent"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}